import React from 'react';
import { formatInrReportCell } from './formatInrReportMoney';

export interface GroupRow {
  label: string;
  monthly: Record<string, number>; // "2025-04" → INR amount
  total: number;
}

interface Props {
  months: string[];        // ["2025-04","2025-05",...,"2026-03"]
  rows: GroupRow[];
  columnTotals: Record<string, number>;
  grandTotal: number;
}

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function monthLabel(ym: string): string {
  const [y, m] = ym.split('-');
  return `${MONTH_SHORT[Number(m) - 1]} '${y.slice(2)}`;
}

export const ExpenseTable: React.FC<Props> = ({ months, rows, columnTotals, grandTotal }) => {
  if (rows.length === 0) {
    return (
      <div className="py-12 text-center text-slate-400 text-sm">
        No paid invoices found for this period
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse min-w-[900px]">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-2.5 px-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 sticky left-0 bg-white min-w-[140px]">
              Group
            </th>
            {months.map(ym => (
              <th
                key={ym}
                className="text-right py-2.5 px-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 whitespace-nowrap min-w-[72px]"
              >
                {monthLabel(ym)}
              </th>
            ))}
            <th className="text-right py-2.5 px-3 text-[10px] font-bold uppercase tracking-widest text-blue-600 whitespace-nowrap min-w-[80px]">
              Total
            </th>
          </tr>
        </thead>

        {/* ── Data rows ──────────────────────────────────────────────── */}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.label}
              className={`border-b border-slate-100 hover:bg-blue-50/30 transition-colors ${
                i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'
              }`}
            >
              <td className="py-2.5 px-3 font-semibold text-slate-700 sticky left-0 bg-inherit">
                {row.label}
              </td>
              {months.map(ym => {
                const v = row.monthly[ym] || 0;
                return (
                  <td
                    key={ym}
                    className={`py-2.5 px-2 text-right tabular-nums ${
                      v === 0 ? 'text-slate-300' : 'text-slate-700'
                    }`}
                  >
                    {formatInrReportCell(v)}
                  </td>
                );
              })}
              <td className="py-2.5 px-3 text-right font-bold text-blue-700 tabular-nums">
                {formatInrReportCell(row.total)}
              </td>
            </tr>
          ))}
        </tbody>

        {/* ── Totals footer ───────────────────────────────────────────── */}
        <tfoot>
          <tr className="border-t-2 border-slate-200 bg-slate-50">
            <td className="py-3 px-3 font-bold text-slate-800 sticky left-0 bg-slate-50 text-[11px] uppercase tracking-wide">
              Total
            </td>
            {months.map(ym => (
              <td
                key={ym}
                className="py-3 px-2 text-right font-bold text-slate-800 tabular-nums"
              >
                {formatInrReportCell(columnTotals[ym] || 0)}
              </td>
            ))}
            <td className="py-3 px-3 text-right font-bold text-blue-700 tabular-nums text-[13px]">
              {formatInrReportCell(grandTotal)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
